import { DateTime } from "luxon";
import { AvailableDate, DateRange, TimeInterval } from "../types";

export const isDateRange = (d: AvailableDate): d is DateRange =>
  typeof d === "object" && "startDate" in d;

export const countAvailableDays = (dates: AvailableDate[]): number =>
  dates.reduce((total, d) => {
    if (typeof d === "string") return total + 1;
    if (isDateRange(d)) {
      const start = DateTime.fromISO(d.startDate).startOf("day");
      const end = DateTime.fromISO(d.endDate).startOf("day");
      return total + Math.round(end.diff(start, "days").days) + 1;
    }
    // TimeInterval counts as one day
    return total + 1;
  }, 0);

export const filterPastAvailableDates = (dates: AvailableDate[]): AvailableDate[] => {
  const today = DateTime.now().startOf("day");
  return dates.reduce<AvailableDate[]>((acc, d) => {
    if (typeof d === "string") {
      if (DateTime.fromISO(d).startOf("day") >= today) acc.push(d);
    } else if (isDateRange(d)) {
      const end = DateTime.fromISO(d.endDate).startOf("day");
      if (end < today) return acc;
      const start = DateTime.fromISO(d.startDate).startOf("day");
      acc.push(start < today ? { startDate: today.toISO()!, endDate: d.endDate } : d);
    } else {
      if (DateTime.fromISO(d.start).startOf("day") >= today) acc.push(d);
    }
    return acc;
  }, []);
};

export const expandAvailableDates = (dates: AvailableDate[]): Array<string | TimeInterval> => {
  const result: Array<string | TimeInterval> = [];
  for (const d of dates) {
    if (!isDateRange(d)) {
      result.push(d);
      continue;
    }
    const start = DateTime.fromISO(d.startDate).startOf("day");
    const end = DateTime.fromISO(d.endDate).startOf("day");
    const days = Math.round(end.diff(start, "days").days);
    for (let i = 0; i <= days; i++) {
      result.push(start.plus({ days: i }).toISO()!);
    }
  }
  return result;
};

export const consolidateAvailableDates = (dates: AvailableDate[]): AvailableDate[] => {
  const strings = dates.filter((d): d is string => typeof d === "string");
  const others = dates.filter((d) => typeof d !== "string");

  const sorted = strings
    .map((s) => DateTime.fromISO(s))
    .filter((d) => d.isValid)
    .sort((a, b) => a.toMillis() - b.toMillis());

  const ranges: AvailableDate[] = [];
  let rangeStart: DateTime | null = null;
  let rangeEnd: DateTime | null = null;

  for (const dt of sorted) {
    if (!rangeStart || !rangeEnd) {
      rangeStart = dt;
      rangeEnd = dt;
    } else if (dt.startOf("day").diff(rangeEnd.startOf("day"), "days").days <= 1) {
      rangeEnd = dt;
    } else {
      ranges.push(
        rangeStart.startOf("day").toISODate() === rangeEnd.startOf("day").toISODate()
          ? rangeStart.toISO()!
          : { startDate: rangeStart.toISO()!, endDate: rangeEnd.toISO()! },
      );
      rangeStart = dt;
      rangeEnd = dt;
    }
  }

  if (rangeStart && rangeEnd) {
    ranges.push(
      rangeStart.startOf("day").toISODate() === rangeEnd.startOf("day").toISODate()
        ? rangeStart.toISO()!
        : { startDate: rangeStart.toISO()!, endDate: rangeEnd.toISO()! },
    );
  }

  return [...ranges, ...others];
};
